import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
  requiredPermission?: string;
}

export function ProtectedRoute({
  children,
  requireAdmin = false,
  requireSuperAdmin = false,
  requiredPermission,
}: ProtectedRouteProps) {
  const { user, isLoading, isAdmin, isSuperAdmin, hasPermission } = useAuth();

  if (isLoading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center">
        <div className="text-white text-2xl font-vt323 animate-pulse">
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  if (requireSuperAdmin && !isSuperAdmin) {
    return (
      <div className="w-screen h-screen flex items-center justify-center">
        <div className="text-white text-2xl font-vt323">
          Access denied. Super-admin privileges required.
        </div>
      </div>
    );
  }

  if (requireAdmin && !isAdmin) {
    return (
      <div className="w-screen h-screen flex items-center justify-center">
        <div className="text-white text-2xl font-vt323">
          Access denied. Admin privileges required.
        </div>
      </div>
    );
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className="w-screen h-screen flex items-center justify-center">
        <div className="text-white text-2xl font-vt323">
          Access denied. You do not have permission to access this feature.
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
